import React, { useCallback, useEffect, useState } from 'react'
import cn from 'classnames'
import { PlayPauseButton } from '../../../play-pause'
import { useIsInternalTesting } from '../../hooks/use-internal-testing'

import { VideoPlayer, VideoPlayerProps } from '../video-player'
import { useLazyLoadAnimationVideo } from './lazy-load-helpers'
import styles from './style.module.scss'
import { useParallaxContainerContext, useParallaxSlideContext } from '../../../../contexts/parallax-context'
import { ControlsLocation } from '../../../../lib/types'

const controlsLocationStyles = {
  [ControlsLocation.OutsideBottomLeft]: styles.outsideBottomLeft,
  [ControlsLocation.InsideBottomLeft]: styles.insideBottomLeft,
  [ControlsLocation.InsideTopRight]: styles.insideTopRight,
  [ControlsLocation.InsideBottomRight]: styles.insideBottomRight,
}

interface AnimationVideoPlayerProps extends VideoPlayerProps {
  lazyLoad?: boolean
  controlsLocation?: ControlsLocation
  animationInternalName?: string
}

export const AnimationVideoPlayer: React.FC<AnimationVideoPlayerProps> = ({
  lazyLoad = true,
  controlsLocation = ControlsLocation.OutsideBottomLeft,
  animationInternalName,
  ...props
}) => {
  const [videoRef, renderVideoSource] = useLazyLoadAnimationVideo(lazyLoad)
  const isInternalTesting = useIsInternalTesting()
  const shouldAutoPlay = isInternalTesting ? false : props.autoPlay
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(shouldAutoPlay)
  const [showPlayPauseButton, setShowPlayPauseButton] = useState(false)

  const onPlayPauseClick = useCallback(() => {
    if (!videoRef.current) {
      return
    }

    if (isAnimationPlaying) {
      videoRef.current.pause()
      setIsAnimationPlaying(false)
    } else {
      videoRef.current.play()
      setIsAnimationPlaying(true)
    }
  }, [isAnimationPlaying, videoRef])

  const { containerName: parallaxContainerContextName, activeSlideIndex } = useParallaxContainerContext()
  const { containerName: parallaxSlideContextName, index } = useParallaxSlideContext()

  useEffect(() => {
    if (videoRef.current) {
      if (
        parallaxContainerContextName &&
        activeSlideIndex > -1 &&
        parallaxContainerContextName === parallaxSlideContextName
      ) {
        if (activeSlideIndex === index) {
          videoRef.current.currentTime = 0
          videoRef.current.play()
          setIsAnimationPlaying(true)
        }
      }
    }
  }, [activeSlideIndex, parallaxContainerContextName, index, parallaxSlideContextName, videoRef])

  const onVideoEnded = () => {
    if (props.loop) {
      return
    }

    setIsAnimationPlaying(false)
  }

  const onLoadedMetadata = (target: HTMLVideoElement) => {
    // If video is longer than 5 seconds, show the play pause button
    if (target.duration > 5) {
      setShowPlayPauseButton(true)
    }
  }

  const playPauseAriaLabel = isAnimationPlaying ? 'Pause animation' : 'Play animation'
  const shouldShowPlayPauseButton = showPlayPauseButton && !parallaxContainerContextName

  return (
    <div className={styles.videoWrapper} data-component-id={animationInternalName}>
      <VideoPlayer
        {...props}
        ref={videoRef}
        autoPlay={shouldAutoPlay}
        muted
        playsInline
        renderVideoSource={renderVideoSource}
        onEnded={onVideoEnded}
        onLoadedMetadata={onLoadedMetadata}
      />
      {shouldShowPlayPauseButton && (
        <div className={cn(styles.playPauseWrapper, controlsLocationStyles[controlsLocation])}>
          <PlayPauseButton
            state={isAnimationPlaying ? 'playing' : 'paused'}
            onClick={onPlayPauseClick}
            backgroundColor="#FFF"
            ariaLabel={playPauseAriaLabel}
          />
        </div>
      )}
    </div>
  )
}
